import { useState, useEffect } from 'react';
import organisationSettingsService from '../services/organisationSettingsService';
import useAuthStore from '../store/authStore';

const DEFAULT_PRODUCT_NAME = 'Lumina AI';

interface WhitelabelSettings {
  productName: string;
  primaryLogoUrl: string | null;
  secondaryLogoUrl: string | null;
  isLoading: boolean;
  isWhitelabeled: boolean;
}

// Cache the whitelabel settings to avoid repeated API calls
let cachedSettings: WhitelabelSettings | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useWhitelabel = (): WhitelabelSettings => {
  const { user } = useAuthStore();
  const [settings, setSettings] = useState<WhitelabelSettings>(() => {
    // Use cached settings if available and not expired
    if (cachedSettings && Date.now() - cacheTimestamp < CACHE_DURATION) {
      return cachedSettings;
    }
    return {
      productName: DEFAULT_PRODUCT_NAME,
      primaryLogoUrl: null,
      secondaryLogoUrl: null,
      isLoading: true,
      isWhitelabeled: false
    };
  });

  useEffect(() => {
    const loadWhitelabelSettings = async () => {
      // Don't fetch if user is not logged in
      if (!user) {
        setSettings({
          productName: DEFAULT_PRODUCT_NAME,
          primaryLogoUrl: null,
          secondaryLogoUrl: null,
          isLoading: false,
          isWhitelabeled: false
        });
        return;
      }

      // Use cache if available and not expired
      if (cachedSettings && Date.now() - cacheTimestamp < CACHE_DURATION) {
        setSettings(cachedSettings);
        return;
      }

      try {
        const orgSettings = await organisationSettingsService.getOrganisationSettings();
        
        const newSettings: WhitelabelSettings = {
          productName: orgSettings.whitelabelProductName || DEFAULT_PRODUCT_NAME,
          primaryLogoUrl: orgSettings.whitelabelPrimaryLogoUrl || null,
          secondaryLogoUrl: orgSettings.whitelabelSecondaryLogoUrl || null,
          isLoading: false,
          isWhitelabeled: !!(orgSettings.whitelabelProductName || orgSettings.whitelabelPrimaryLogoUrl || orgSettings.whitelabelSecondaryLogoUrl)
        };

        // Update cache
        cachedSettings = newSettings;
        cacheTimestamp = Date.now();
        
        setSettings(newSettings);
      } catch (error) {
        console.error('Error loading whitelabel settings:', error);
        setSettings({
          productName: DEFAULT_PRODUCT_NAME,
          primaryLogoUrl: null,
          secondaryLogoUrl: null,
          isLoading: false,
          isWhitelabeled: false
        });
      }
    };

    loadWhitelabelSettings();
  }, [user]);

  return settings;
};

// Function to invalidate cache (call when whitelabel settings are updated)
export const invalidateWhitelabelCache = () => {
  cachedSettings = null;
  cacheTimestamp = 0;
};

// Function to get product name synchronously (uses cache or default)
export const getProductName = (): string => {
  return cachedSettings?.productName || DEFAULT_PRODUCT_NAME;
};

export default useWhitelabel;











